import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLoading } from './LoadingContext';

const CurrencyContext = createContext();
const BOOTSTRAP_FETCH_TIMEOUT_MS = 8000;

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const { track } = useLoading();
  const [currency, setCurrency] = useState({
    symbol: '₹',
    code: 'INR',
    name: 'Indian Rupee',
    country: 'IN'
  });
  const [loading, setLoading] = useState(true);

  const fetchWithTimeout = useCallback(async (url, options = {}, timeoutMs = BOOTSTRAP_FETCH_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  const fetchCurrencySettings = useCallback(async () => {
    try {
      await track(async () => {
        const response = await fetchWithTimeout('/api/settings');
        const data = await response.json();
        
        if (data.success && data.data.settings.currency) {
          setCurrency(data.data.settings.currency.value);
        }
      });
    } catch (error) {
      console.error('Error fetching currency settings:', error);
      // Keep default currency (INR) if fetch fails
    } finally {
      setLoading(false);
    }
  }, [fetchWithTimeout, track]);

  useEffect(() => {
    fetchCurrencySettings();
  }, [fetchCurrencySettings]);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return 'N/A';
    
    // Format according to currency
    if (currency.code === 'INR') {
      // Indian formatting: ₹1,23,456.78
      return `${currency.symbol}${numPrice.toLocaleString('en-IN', {
        minimumFractionDigits: numPrice % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      })}`;
    } else if (currency.code === 'USD') {
      // US formatting: $1,234.56
      return `${currency.symbol}${numPrice.toLocaleString('en-US', {
        minimumFractionDigits: numPrice % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      })}`;
    } else {
      // Default formatting
      return `${currency.symbol}${numPrice.toLocaleString(undefined, {
        minimumFractionDigits: numPrice % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      })}`;
    }
  };

  const refreshCurrency = () => {
    fetchCurrencySettings();
  };

  const value = {
    currency,
    loading,
    formatPrice,
    refreshCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;